/**
 * Syncs buy-box rating from REVIEWS.io API (source of truth).
 * Zero reviews → "Not yet reviewed" (not hidden).
 */
(() => {
  const API = 'https://api.reviews.io/reviews';
  const EMPTY_LABEL = 'Not yet reviewed';

  function readAttr(el, name) {
    const value = el.getAttribute(name);
    return value == null ? '' : String(value).trim();
  }

  function apiSku(el) {
    const main = readAttr(el, 'data-prl-sku');
    const all = readAttr(el, 'data-prl-skus');
    const multi = el.getAttribute('data-prl-multi-variant') === 'true';
    if (!multi) return main || all;
    return all || main;
  }

  function ensureEmptyMarkup(el) {
    let stars = el.querySelector('.prl-rating__stars');
    if (!stars) {
      stars = document.createElement('span');
      stars.className = 'prl-rating__stars';
      stars.setAttribute('aria-hidden', 'true');
      stars.innerHTML = '<span class="prl-rating__stars-base">★★★★★</span><span class="prl-rating__stars-fill">★★★★★</span>';
      el.insertBefore(stars, el.firstChild);
    }
    stars.hidden = false;
    stars.style.setProperty('--rating-percent', '0%');

    const meta = el.querySelector('.prl-rating__meta');
    if (meta) meta.hidden = true;

    let emptyEl = el.querySelector('[data-prl-empty]');
    if (!emptyEl) {
      emptyEl = document.createElement('span');
      emptyEl.className = 'prl-rating__empty';
      emptyEl.setAttribute('data-prl-empty', '');
      el.appendChild(emptyEl);
    }
    emptyEl.textContent = EMPTY_LABEL;
    emptyEl.hidden = false;
  }

  function applyEmpty(el) {
    el.classList.remove('prl-rating--pending');
    el.classList.add('prl-rating--empty');
    el.setAttribute('aria-label', EMPTY_LABEL);
    el.removeAttribute('aria-hidden');
    ensureEmptyMarkup(el);
  }

  function applyRating(el, average, count) {
    const avg = Number(average || 0);
    const n = Number(count || 0);

    if (n <= 0) {
      applyEmpty(el);
      return;
    }

    el.classList.remove('prl-rating--empty', 'prl-rating--pending');

    const emptyEl = el.querySelector('[data-prl-empty]');
    const starsWrap = el.querySelector('.prl-rating__stars');
    const meta = el.querySelector('.prl-rating__meta');
    const avgEl = el.querySelector('[data-prl-average]');
    const countEl = el.querySelector('[data-prl-count]');

    if (emptyEl) emptyEl.hidden = true;
    if (starsWrap) starsWrap.hidden = false;
    if (meta) meta.hidden = false;

    if (!starsWrap || !meta) {
      el.innerHTML = `
        <span class="prl-rating__stars" style="--rating-percent: ${Math.max(0, Math.min(100, (avg / 5) * 100))}%;" aria-hidden="true">
          <span class="prl-rating__stars-base">★★★★★</span>
          <span class="prl-rating__stars-fill">★★★★★</span>
        </span>
        <span class="prl-rating__meta">
          <span data-prl-average>${avg.toFixed(1)}</span>
          <span>(</span><span data-prl-count>${n}</span><span> reviews)</span>
        </span>
      `;
    } else {
      if (avgEl) avgEl.textContent = avg.toFixed(1);
      if (countEl) countEl.textContent = String(n);
      starsWrap.style.setProperty(
        '--rating-percent',
        `${Math.max(0, Math.min(100, (avg / 5) * 100))}%`
      );
    }

    const labelCount = n === 1 ? 'review' : 'reviews';
    el.setAttribute(
      'aria-label',
      `${avg.toFixed(1)} out of 5 stars from ${n} ${labelCount}`
    );
    el.dataset.prlMetaCount = String(n);
    el.removeAttribute('aria-hidden');
  }

  function applyMetafieldFallback(el) {
    const metaCount = Number(el.dataset.prlMetaCount || 0);
    const metaAvg = Number(el.dataset.prlMetaAverage || 0);
    if (metaCount > 0) {
      applyRating(el, metaAvg, metaCount);
      return;
    }
    applyEmpty(el);
  }

  async function fetchSummary(store, sku) {
    const url = new URL(API);
    url.searchParams.set('store', store);
    url.searchParams.set('type', 'product_review');
    url.searchParams.set('sku', sku);
    url.searchParams.set('per_page', '1');
    url.searchParams.set('page', '1');

    const response = await fetch(url.toString(), { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('fetch failed');
    return response.json();
  }

  async function verifyRating(el) {
    const store = readAttr(el, 'data-prl-store-id');
    let sku = apiSku(el);
    const primary = readAttr(el, 'data-prl-sku');

    if (!store || !sku) {
      el.remove();
      return;
    }

    try {
      let json;
      try {
        json = await fetchSummary(store, sku);
      } catch (_err) {
        if (primary && primary !== sku) {
          sku = primary;
          json = await fetchSummary(store, sku);
        } else {
          throw _err;
        }
      }

      const reviews = Array.isArray(json.reviews) ? json.reviews : [];
      const apiCount = Math.max(
        Number(json.review_count || 0),
        Number(json.results_count || 0),
        reviews.length > 0 ? reviews.length : 0
      );
      const apiAvg = Number(json.average_rating || 0);

      applyRating(el, apiAvg, apiCount);
    } catch (_err) {
      applyMetafieldFallback(el);
    }
  }

  function boot() {
    const run = () => {
      document.querySelectorAll('[data-prl-rating]').forEach(verifyRating);
    };

    if ('requestIdleCallback' in window) {
      requestIdleCallback(run, { timeout: 2000 });
    } else {
      setTimeout(run, 0);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
